/**
 * CreditsBadge Component
 *
 * Displays remaining credits (paid tier) or messages (free tier) in the sidebar.
 * Color states: green (>50%), amber (20-50%), red (<20%), gray (loading/error).
 */

import { useEffect, useState, useCallback } from 'react';
import { Zap } from 'lucide-react';
import { colors } from '@/theme';
import { creditsAPI, type CreditBalance } from '@/services/api';
import { wsService } from '@/services/websocket';
import { useSubscriptionStore } from '@/stores/subscription';

export function CreditsBadge() {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const { isFreeTier } = useSubscriptionStore();

  const fetchBalance = useCallback(async () => {
    try {
      const data = await creditsAPI.getBalance();
      setBalance(data);
    } catch {
      // Silently fail — credits badge is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    // Refresh every 60s
    const interval = setInterval(fetchBalance, 60_000);

    // Refresh when a task completes (credits consumed)
    const unsubscribeTasks = wsService.subscribeTasks(() => {
      fetchBalance();
    });

    // Refresh when Alisha sends a message (credits consumed for free tier messages)
    const unsubscribeAlisha = wsService.subscribeExchangeChannel('iris', () => {
      fetchBalance();
    });

    // Immediate refresh triggered by chat stores (no WebSocket roundtrip needed)
    const handleCreditsRefresh = () => fetchBalance();
    window.addEventListener('credits-refresh', handleCreditsRefresh);

    return () => {
      clearInterval(interval);
      unsubscribeTasks();
      unsubscribeAlisha();
      window.removeEventListener('credits-refresh', handleCreditsRefresh);
    };
  }, [fetchBalance]);

  const isFree = isFreeTier();

  if (loading || !balance) {
    return (
      <div
        className="flex items-center gap-3 px-3 py-3 rounded-lg"
        style={{ background: colors.obsidian, border: `1px solid ${colors.forgeSteel}` }}
      >
        <Zap size={24} style={{ color: colors.secondaryText }} />
        <span className="text-xs flex-1 text-center" style={{ color: colors.secondaryText }}>
          {loading ? '...' : (isFree ? 'Messages unavailable' : 'Credits unavailable')}
        </span>
      </div>
    );
  }

  const { displayAvailable, monthlyCredits } = balance;
  const maxCredits = Math.max(monthlyCredits, 1);

  // Both tiers: show percentage of remaining (bar empties as consumed)
  // Use displayAvailable to exclude grace credits from UI
  const pct = (displayAvailable / maxCredits) * 100;

  // Color coding based on remaining capacity
  const badgeColor =
    pct > 70 ? colors.neonMint :      // Healthy (green)
    pct > 50 ? '#FBBF24' :            // Caution (yellow)
    pct > 20 ? colors.moltenGold :    // Warning (amber)
    '#EF4444';                         // Critical (red)

  return (
    <div
      className="flex items-center gap-3 px-3 py-3 rounded-lg"
      style={{ background: colors.obsidian, border: `1px solid ${colors.forgeSteel}` }}
    >
      <Zap size={24} style={{ color: badgeColor }} />

      <div className="flex-1 flex flex-col gap-2">
        <span className="text-xs font-medium text-left" style={{ color: colors.sandstone }}>
          {isFree ? (
            `${displayAvailable} message${displayAvailable !== 1 ? 's' : ''} left`
          ) : (
            `${displayAvailable.toLocaleString()} credit${displayAvailable !== 1 ? 's' : ''} left`
          )}
        </span>

        {/* Progress bar */}
        <div
          className="w-full h-0.5 rounded-full"
          style={{ background: colors.forgeSteel }}
        >
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${Math.min(pct, 100)}%`,
              background: badgeColor,
            }}
          />
        </div>
      </div>
    </div>
  );
}
